import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { toggleTaskSelection } from 'bundles/apex/deals/show/store/planner_bulk_actions/actions';

import pluralize from 'pluralize';

import { Alert } from 'react-bootstrap-5';
import Button from 'shared/button';
import Modal from 'components/modal';

export function MarkCompleteModal({ selection, show, tasks, toggleTaskSelection, onClose, onUpdate }) {
  const [errors, setErrors] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const unmarkableTasks = useMemo(() => tasks.filter((task) => {
    return selection[task.id] && task.requiresAttachment && !task.attachments.length;
  }), [show]);

  const markableTaskCount = useMemo(
    () => Object.keys(selection).length - unmarkableTasks.length,
    [show]
  );

  const updateTasks = async () => {
    setIsUpdating(true);
    if (unmarkableTasks.length > 0) {
      unmarkableTasks.forEach(task => toggleTaskSelection(task));
    }
    const message =
      `${markableTaskCount} ${pluralize('task', markableTaskCount)} ` +
      `${pluralize('has', markableTaskCount)} been marked as complete!`;

    const response = await onUpdate({ completed: true }, message);
    setIsUpdating(false);

    if (response.errors) {
      setErrors(response.errors);
      return;
    }

    onClose();
  };

  const alertTitle = `Could not mark ${pluralize('task', Object.keys(selection).length)} as complete`;

  return (
    <Modal
      show={show}
      onHide={onClose}
      onShow={() => setErrors([])}
    >
      <Modal.Header closeButton>Mark as Complete</Modal.Header>

      <Modal.Body>
        <Alert className="mb-4" variant="warning">
          <h6><strong>Whoops -</strong> {alertTitle}</h6>
          <ul>
            {errors.map((error, i) => <li key={i}>{error}</li>)}
          </ul>
        </Alert>
        {markableTaskCount === 0 ? <p>All tasks require an attachment and cannot be marked as complete.</p> : (
          <React.Fragment>
            {unmarkableTasks.length > 0 && (
              <p>
                <strong>{unmarkableTasks.length}</strong> {pluralize('task', unmarkableTasks.length)} cannot
                be marked complete because an attachment is required.
              </p>
            )}
            <p>
              Are you sure you want to
              mark <strong>{markableTaskCount}</strong> {pluralize('task', markableTaskCount)} as complete?
            </p>
          </React.Fragment>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button.Cancel
          disabled={isUpdating}
          label={markableTaskCount === 0 ? 'Close' : 'Cancel'}
          onClick={onClose}
        />
        {markableTaskCount > 0 && (
          <Button.Primary
            disabled={isUpdating}
            label="Yes, Update"
            onClick={updateTasks}
          />)}
      </Modal.Footer>
    </Modal>
  );
}

MarkCompleteModal.propTypes = {
  selection: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  tasks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    requiresAttachment: PropTypes.bool.isRequired,
    attachments: PropTypes.array.isRequired,
  })),
  toggleTaskSelection: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  selection: state.plannerBulkActions.selection,
  tasks: state.tasks,
});

const mapDispatchToProps = {
  toggleTaskSelection
};

export default connect(mapStateToProps, mapDispatchToProps)(MarkCompleteModal);
